import { echoReducer } from "./reducers/echo";
import { createStore } from ".";
import { actions } from "./index";

import { breakpoints } from "../../design-tokens/sizes";
import { debounce } from "../utilities";

// I took our breakpoints and made sizes from them.  For example, xs is 0 - 480px, sm is 481px - 768px, md is 769px - 1024px, etc.

const xs = parseInt(breakpoints["breakpoints-xs"]());
const sm = parseInt(breakpoints["breakpoints-sm"]());
const md = parseInt(breakpoints["breakpoints-md"]());
const lg = parseInt(breakpoints["breakpoints-lg"]());
const xl = parseInt(breakpoints["breakpoints-xl"]());
const twoXl = parseInt(breakpoints["breakpoints-2xl"]());
const threeXl = parseInt(breakpoints["breakpoints-3xl"]());
const fourXl = parseInt(breakpoints["breakpoints-4xl"]());
const fiveXl = parseInt(breakpoints["breakpoints-5xl"]());

const breakpointsObject = {
    xs: [xs, sm],
    sm: [sm + 1, md],
    md: [md + 1, lg],
    lg: [lg + 1, xl],
    xl: [xl + 1, twoXl],
    "2xl": [twoXl + 1, threeXl],
    "3xl": [threeXl + 1, fourXl],
    "4xl": [fourXl + 1, fiveXl],
    "5xl": [fiveXl + 1],
};

// This function uses our breakpoints to return a string indicating which screen size you are in, for ex. 'breakpoints - sm'.
const returnBreakpoint = () => {
    const values = Object.values(breakpoints).map((bk) => {
        return parseInt(bk().replace("px", ""));
    });

    for (const [key, value] of Object.entries(breakpoints)) {
        const currentValue = parseInt(value());

        const nextIndex = values.indexOf(parseInt(value())) + 1;

        const nextValue = values[nextIndex];

        if (currentValue <= window.innerWidth && nextValue >= window.innerWidth) {
            return key;
        }
    }
};

let screenBreakpointInstance;

export const getScreenBreakpointInstance = () => {
    // If an instance of screenBreakpoint already exists in the application, then return it.
    if (screenBreakpointInstance) return screenBreakpointInstance;

    // Create the store and use an empty object for the initial state.
    const screenBreakpointStore = createStore(echoReducer, {});

    // Once the page is loaded, then find out what the screen size is and update the state with the current boolean values
    // for each of the window sizes.
    for (const breakpoint in breakpointsObject) {
        const mq =
            breakpointsObject[breakpoint].length > 1
                ? window.matchMedia(
                      `(min-width: ${breakpointsObject[breakpoint][0]}px) and (max-width: ${breakpointsObject[breakpoint][1]}px)`
                  )
                : window.matchMedia(`(min-width: ${breakpointsObject[breakpoint][0]}px)`);
        // Grab current boolean values for each possible window size.
        screenBreakpointStore.dispatch(actions.breakpointReached({ [breakpoint]: mq.matches }));
        // Add an event listener to each window size to detect when a new window size has been entered into.
        mq.addEventListener("change", () => {
            screenBreakpointStore.dispatch(
                actions.breakpointReached({
                    width: window.innerWidth,
                    height: window.innerHeight,
                    [breakpoint]: mq.matches,
                })
            );
        });
    }

    // Return this newly created instance which will now serve the entire application.
    return (screenBreakpointInstance = {
        subscribe: screenBreakpointStore.subscribe,
        unsubscribe: screenBreakpointStore.unsubscribe,
        getState: screenBreakpointStore.getState,
    });
};

let screenResizeInstance;

export const getScreenResizeInstance = () => {
    // If an instance of screenResize already exists in the application, then return it.
    if (screenResizeInstance) return screenResizeInstance;

    // If is does not, create an instance of resize store.
    const screenResizeStore = createStore(echoReducer, {
        width: window.innerWidth,
        height: window.innerHeight,
    });
    // Since 'resize' event is not fired on page load, have to have a separate page load listener to get initial screen dimensions.
    window.addEventListener("load", () => {
        screenResizeStore.dispatch(
            actions.screenSize({
                width: window.innerWidth,
                height: window.innerHeight,
            })
        );
    });

    // Use 'resize' event  and debounce method to update dimensions of window after a 200ms delay so in case the user is resizing a lot,
    // only the final stopping point is captured and sent to the reducer.
    window.addEventListener(
        "resize",
        debounce(() => {
            screenResizeStore.dispatch(
                actions.screenSize({
                    width: window.innerWidth,
                    height: window.innerHeight,
                    currentBreakpoint: returnBreakpoint(),
                })
            );
        }, 200)
    );
    // Return this newly created instance which will now serve the entire application.
    return (screenResizeInstance = {
        subscribe: screenResizeStore.subscribe,
        unsubscribe: screenResizeStore.unsubscribe,
        getState: screenResizeStore.getState,
    });
};
